import { AlternativeEditorDialogViewModel } from '@Alternatives/Alternatives/Components/AlternativeEditorDialog';
import AlternativeEditorDialog from '@Alternatives/Alternatives/Components/AlternativeEditorDialog.vue';
import { AttributeEditorDialogViewModel } from '@Alternatives/Attributes/Components/AttributeEditorDialog';
import AttributeEditorDialog from '@Alternatives/Attributes/Components/AttributeEditorDialog.vue';
import AttributesQueries from '@Alternatives/Attributes/Domain/queries-vue-component';
import { AttributeEditorReadModel, AttributeReadModel } from '@Alternatives/Attributes/Domain/readmodels';
import { ICommandBase } from '@Alternatives/cqrs';
import { FormEditorSaveEventPayload, FormEditorViewModel } from '@Alternatives/Forms/Components/FormEditor';
import FormEditor from '@Alternatives/Forms/Components/FormEditor.vue';
import { RenameFormCommandV1, UpdateFormDefinitionCommandV1 } from '@Alternatives/Forms/Domain/commands-core';
import FormsCommands from '@Alternatives/Forms/Domain/commands-vue-component';
import FormsQueries from '@Alternatives/Forms/Domain/queries-vue-component';
import { FormDesignerAttributeReadModel, FormDesignerReadModel } from '@Alternatives/Forms/Domain/readmodels';
import { AttributeFormDefinitionDTO } from '@Alternatives/Model';
import { EditorState, ellipsis, generateName, numCompare, strCompareLowerCase } from '@common/Common';
import PortfolioNavigation from "@common/Components/PortfolioNavigation.vue";
import { ICommandStatus } from '@cqrs/cqrs';
import Component, { mixins } from 'vue-class-component';


@Component({
    components: { PortfolioNavigation, AttributeEditorDialog, AlternativeEditorDialog, FormEditor },
    watch: {
        searchPhrase: 'onSearchPhraseChanged'
    }
})
export default class EditFormPage extends mixins(FormsQueries, FormsCommands, AttributesQueries)
{
    get workspaceId(): number { return Number.parseInt(this.$route.params.workspaceId); }
    get projectId(): number { return Number.parseInt(this.$route.params.projectId); }
    get formId(): number { return Number.parseInt(this.$route.params.formId); }


    rm: FormDesignerReadModel | null = null;
    get alternativesListId(): number { return this.rm.AlternativesListId; }
    get alternativeLabel(): string { return this.rm.AlternativeLabel; }
    get attributes() { return this.rm.Attributes ?? []; }


    get formName(): string { return this.rm.Name; }
    get blurbError(): string | null { return this.blurb?.length > 100 ? "Up to 100 characters" : null; }
    get selectedField(): FormDesignerAttributeReadModel | null { return this.attributes.find(a => a.Id === this.selectedAttributeId) ?? null; }
    get draggedField(): FormDesignerAttributeReadModel | null { return this.attributes.find(a => a.Id === this.draggingAttributeId) ?? null; }
    get formSectionsView(): FormSectionVM[] { return this.formSections.sort((a, b) => strCompareLowerCase(a.Name, b.Name)); }

    attributeEditorDialog = new AttributeEditorDialogViewModel();
    alternativeEditorDialog = new AlternativeEditorDialogViewModel();
    formSectionEditor = new FormSectionEditorViewModel();
    formEditor = new FormEditorViewModel();

    searchPhrase: string | null = null;
    selectedAttributeId: number | null = null;
    draggingAttributeId: number | null = null;

    attributesSections: AttributesSectionVM[] = [];
    formSections: FormSectionVM[] = [];
   
    blurb: string | null = null;
    nameReadonly: boolean = false;
    includeDescription: boolean = false;
    descriptionReadonly: boolean = false;
    includeStatus: boolean = false;
    statusReadonly: boolean = false;

    awaitingPreview: boolean = false;
    saving: boolean = false;

    //watcher searchPhrase
    onSearchPhraseChanged(val: string, oldVal: string)
    {
        this.attributesSections.forEach(a => a.searchPhrase = val);

        if (val)
        {
            this.attributesSections.forEach(a => a.Collapsed = false);
        }
        else
        {
            this.attributesSections.forEach(a => a.Collapsed = true);
        }
    }


    handleBack()
    {
        this.$router.go(-1);
    }

    showAttributeCreator()
    {
        this.attributeEditorDialog.showCreator({
            defaultName: generateName("Attribute"),

            allowChangeAttributeType: true,
            allowMultipleValuesEnabled: true,
            allowInputTypeEvaluate: true,
        });

        this.runAttributeEditorQueryV1(this.workspaceId, this.projectId, null);
    }

    showFormPreview()
    {
        this.awaitingPreview = true;
        this.handleUpdateFormDefinitionCommand();        
    }

    showFormEditor()
    {
        this.formEditor.showEditor(this.formId, this.formName);
    }

    showFormSectionCreator()
    {
        this.formSectionEditor.showCreator();
    }

    showFormSectionEditor(s: FormSectionVM)
    {
        this.formSectionEditor.showEditor(s.Name);
    }


    ellipsis(str: string, maxLength: number): string { return ellipsis(str, maxLength); }

    isFormSectionEmptyWarningVisible(s: FormSectionVM): boolean { return s.Name !== "" && s.Fields.length === 0; }

    isFormFieldHoverable(field: FormDesignerAttributeReadModel): boolean { return this.draggingAttributeId == null || field.Id === this.draggingAttributeId; }


    handleDropFormSection(event: any, s: FormSectionVM)
    {
        if (this.draggedField.IsIncluded === true)
        {
            const section = this.formSections.find(a => a.Name === this.draggedField.FieldSection);
            section.Fields.remove(this.draggedField);
        }

        s.Fields.unshift(this.draggedField);
        this.draggedField.FieldSection = s.Name;
        this.draggedField.IsIncluded = true;

        s.IsHovering = false;
        this.draggingAttributeId = null;
    }

    handleDropFormField(event: any, targetSection: FormSectionVM, targetField: FormDesignerAttributeReadModel)
    {
        const targetIndex = targetSection.Fields.indexOf(targetField) + 1;

        if (this.draggedField.IsIncluded)
        {
            const prevSection = this.formSections.find(a => a.Name === this.draggedField.FieldSection);

            if (prevSection.Name === targetSection.Name)
            {
                const currIndex = targetSection.Fields.indexOf(this.draggedField);

                prevSection.Fields.remove(this.draggedField);

                if (targetIndex >= currIndex)
                {
                    prevSection.Fields.splice(targetIndex - 1, 0, this.draggedField);
                }
                else
                {
                    prevSection.Fields.splice(targetIndex, 0, this.draggedField);
                }
            }
            else
            {
                this.draggedField.FieldSection = targetSection.Name;
                prevSection.Fields.remove(this.draggedField);
                targetSection.Fields.splice(targetIndex, 0, this.draggedField);
            }
        }
        else
        {
            this.draggedField.FieldSection = targetSection.Name;
            this.draggedField.IsIncluded = true;
            targetSection.Fields.splice(targetIndex, 0, this.draggedField);
        }

        // Reset the hover and dragging states
        targetField.IsHovering = false;
        this.draggingAttributeId = null;
    }

    handleDragStart(event: any, attr: FormDesignerAttributeReadModel)
    {
        this.draggingAttributeId = attr.Id;
    }

    handleDragEnd(e: any, attr: FormDesignerAttributeReadModel)
    {
        this.draggingAttributeId = null;
    }



    handleSelectedFieldIsReadonlyChange()
    {
        if (this.selectedField.IsFieldReadonly)
        {
            this.selectedField.IsFieldRequired = false;
        }
    }

    handleSelectedFieldIsRequiredChange()
    {
        if (this.selectedField.IsFieldRequired)
        {
            this.selectedField.IsFieldReadonly = false;
        }
    }

    handleRemoveField(field: FormDesignerAttributeReadModel)
    {
        if (this.selectedAttributeId === field.Id)
        {
            this.selectedAttributeId = null;
        }

        const section = this.formSections.find(a => a.Name === field.FieldSection);
        section.Fields.remove(field);
        field.IsIncluded = false;
    }

    handleRemoveSection(s: FormSectionVM)
    {
        this.formSections.remove(s);
    }

    handleFormSectionEditorSubmit()
    {
        if (this.formSectionEditor.state === EditorState.Editor)
        {
            if (this.formSectionEditor.name === this.formSectionEditor.oldName)
            {
                this.formSectionEditor.close();
            }
            else if (this.formSections.some(a => a.Name.toLowerCase() === this.formSectionEditor.name.toLowerCase()))
            {
                this.formSectionEditor.nameError = `Section ${this.formSectionEditor.name} already exists.`;
            }
            else
            {
                const s = this.formSections.find(a => a.Name.toLowerCase() === this.formSectionEditor.oldName.toLowerCase());
                s.Fields.forEach(a => a.FieldSection = this.formSectionEditor.name);
                s.Name = this.formSectionEditor.name;

                this.formSectionEditor.close();
            }
        }
        else if (this.formSectionEditor.state === EditorState.Creator)
        {
            this.formSectionEditor.nameError = null;

            if (this.formSections.some(a => a.Name.toLowerCase() === this.formSectionEditor.name.toLowerCase()))
            {
                this.formSectionEditor.nameError = `Section ${this.formSectionEditor.name} already exists.`;
            }
            else
            {
                this.formSections.push(new FormSectionVM(this.formSectionEditor.name, []));

                this.formSectionEditor.close();
            }
        }
    }


    handleFieldEditorFormSubmit()
    {
        this.selectedAttributeId = null;
    }


    handleLoadAttribute(attrId: number)
    {
        this.runAttributeByIdQueryV1(this.workspaceId, this.projectId, this.alternativesListId, attrId);
    }

    handleUpdateFormDefinitionCommand()
    {
        this.saving = true;

        const fields = this.formSectionsView.flatMap(s => s.Fields);
        fields.forEach((f, i) => (f.FieldOrderValue = i));

        const x = this.attributes.filter(a => a.IsIncluded).map(a =>
        {
            return {
                AlternativeAttributeId: a.Id,
                OverrideName: a.FieldName,
                OverrideDescription: a.FieldDescription,
                OverrideSection: a.FieldSection,
                OverrideRequired: a.IsFieldRequired,
                Readonly: a.IsFieldReadonly,
                OverrideOrderValue: a.FieldOrderValue
            }
        });

        this.invokeUpdateFormDefinitionCommandV1(this.workspaceId, this.projectId, this.alternativesListId, this.formId, this.formName, null, this.blurb,
            true, this.nameReadonly, this.descriptionReadonly, this.statusReadonly, this.includeDescription, this.includeStatus, x);
    }

    handleRenameFormCommand(e: FormEditorSaveEventPayload)
    {
        this.invokeRenameFormCommandV1(this.workspaceId, this.projectId, this.formId, e.name);
    }

    mounted()
    {
        document.title = "Create a form | TransparentChoice";
        this.runFormSettingsQueryV1(this.workspaceId, this.projectId, this.formId);
    }

    buildAttributeSections()
    {
        const sectionNames = this.attributes
            .map(a => a.AttributeSection)
            .filter((v, i, sectionsNames) => sectionsNames.indexOf(v) === i)
            .sort((a, b) => strCompareLowerCase(a, b));

        this.attributesSections = sectionNames.map(sectionName => new AttributesSectionVM(sectionName, this.attributes.filter(a => a.AttributeSection === sectionName)));
    }

    onSetModel(model: any, modelName: string) 
    {
        if (modelName === "FormDesignerReadModel")
        {
            const rm = model as FormDesignerReadModel;
            this.rm = rm;

            this.blurb = this.rm.Blurb;
            this.nameReadonly = this.rm.NameReadonly;
            this.includeDescription = this.rm.IncludeDescription;
            this.descriptionReadonly = this.rm.DescriptionReadonly;
            this.includeStatus = this.rm.IncludeStatus;
            this.statusReadonly = this.rm.StatusReadonly;

            this.buildAttributeSections();

            //----Building Form Sections -----

            // Extract sections with original case, including an empty string at the start.
            const sectionsWithOriginalCase = ['', ...this.attributes.filter(a => a.IsIncluded && a.FieldSection).map(a => a.FieldSection)];

            // Deduplicate while preserving original case, based on a case-insensitive comparison.
            const uniqueSections = sectionsWithOriginalCase
                .filter((section, index, array) => array.findIndex(s => s.toLowerCase() === section.toLowerCase()) === index)
                .sort((a, b) => strCompareLowerCase(a, b));

            // Map each unique section to a FormSectionVM, gathering the relevant fields for each.
            this.formSections = uniqueSections.map(sectionName =>
            {
                const fields = this.attributes.filter(a => a.IsIncluded && a.FieldSection.toLowerCase() === sectionName.toLowerCase());
                return new FormSectionVM(sectionName, fields);
            });
        }
        else if (modelName === "AttributeReadModel")
        {
            const rm = model as AttributeReadModel;
            if (this.attributes.some(a => a.Id === rm.Id)) return;

            const x = new FormDesignerAttributeReadModel();
            x.Id = rm.Id;
            x.AttributeName = rm.Name;
            x.AttributeDescription = rm.Description;
            x.AttributeSection = rm.Section;
            x.FieldName = rm.Name;
            x.FieldDescription = rm.Description;
            x.IsFieldReadonly = false;
            x.IsFieldRequired = false;
            x.IsHovering = false;
            x.IsIncluded = false;
            this.attributes.push(x);

            this.buildAttributeSections();
        }
        else if (modelName === "AttributeEditorReadModel")
        {
            const rm = model as AttributeEditorReadModel;
            this.attributeEditorDialog.setData(rm);
        }
    }


    onCommandSucceed(cmd: ICommandBase, correlationId: string, result: any)
    {
        if (RenameFormCommandV1.matchType(cmd))
        {
            this.rm.Name = this.formEditor.name;
            this.formEditor.close();
        }
        else if (UpdateFormDefinitionCommandV1.matchType(cmd))
        {
            this.saving = false;

            if (this.awaitingPreview)
            {
                this.alternativeEditorDialog.showPreview(this.workspaceId, this.projectId, this.alternativesListId, this.formId);
                this.awaitingPreview = false;
            }
        }
    }

    onCommandFailed(status: ICommandStatus)
    {
        if (RenameFormCommandV1.matchType(status.Command))
        {
            this.formEditor.displayValidationErrors(status.ValidationErrors);
        }
    }
}


class AttributesSectionVM
{
    Name: string = null;
    Collapsed: boolean = true;
    attributes: FormDesignerAttributeReadModel[] = [];

    searchPhrase: string | null = null;

    get filteredAttributes()
    {
        return this.searchPhrase
            ? this.attributes.filter(f => f.AttributeName.toLowerCase().includes(this.searchPhrase.toLowerCase()))
            : this.attributes;
    }

    constructor(name: string, attributes: FormDesignerAttributeReadModel[])
    {
        this.Collapsed = true;
        this.attributes = attributes;
        this.Name = name;
    }
}

class FormSectionVM
{
    Name: string = null;
    Fields: FormDesignerAttributeReadModel[] = [];

    IsHovering: boolean = false;

    constructor(name: string, fields: FormDesignerAttributeReadModel[])
    {
        this.Name = name;

        const orderedByOrderValue = fields.filter(a => a.FieldOrderValue != null).sort((a, b) => numCompare(a.FieldOrderValue, b.FieldOrderValue));
        const orderedAlpha = fields.filter(a => a.FieldOrderValue == null).sort((a, b) => strCompareLowerCase(a.FieldName, b.FieldName));

        this.Fields.push(...orderedByOrderValue);
        this.Fields.push(...orderedAlpha);
    }
}

class FormSectionEditorViewModel
{
    state: EditorState = EditorState.Closed;

    name: string | null = null;
    oldName: string | null = null;

    nameError: string | null = null;

    get opened(): boolean { return this.state !== EditorState.Closed; }

    get title(): string | null
    {
        if (this.state === EditorState.Creator)
        {
            return "Create form section";
        }

        if (this.state === EditorState.Editor)
        {
            return "Edit section name";
        }

        return null;
    }

    get confimButtonLabel(): string | null
    {
        if (this.state === EditorState.Creator)
        {
            return "Create";
        }

        if (this.state === EditorState.Editor)
        {
            return "Update";
        }

        return null;
    }

    showCreator()
    {
        this.name = generateName("Form section");
        this.oldName = null;
        this.nameError = null;

        this.state = EditorState.Creator;
    }

    showEditor(name: string)
    {
        this.name = name;
        this.oldName = name;
        this.nameError = null;

        this.state = EditorState.Editor;
    }

    close()
    {
        this.state = EditorState.Closed;

        this.name = null;
        this.oldName = null;
        this.nameError = null;
    }
}

export class SaveFormEventPayload
{
    id: number;
    name: string;
    blurb: string | null;
    nameReadonly: boolean;
    descriptionReadonly: boolean;
    includeDescription: boolean;
    includeStatus: boolean;
    statusReadonly: boolean;
    attributesDefinitions: AttributeFormDefinitionDTO[];

    constructor(
        id: number,
        name: string,
        blurb: string | null,
        nameReadonly: boolean,
        descriptionReadonly: boolean,
        includeDescription: boolean,
        includeStatus: boolean,
        statusReadonly: boolean,
        attributesDefinitions: AttributeFormDefinitionDTO[]
    )
    {
        this.id = id;
        this.name = name;
        this.blurb = blurb;
        this.nameReadonly = nameReadonly;
        this.descriptionReadonly = descriptionReadonly;
        this.includeDescription = includeDescription;
        this.includeStatus = includeStatus;
        this.statusReadonly = statusReadonly;
        this.attributesDefinitions = attributesDefinitions;
    }
}